import React from 'react';
import { MenuButton, MenuGroupFooter, MenuGroupHeader, MenuNavigationItemTypeGroup, MenuNavigationItemTypeItem, MenuNavigationItemTypeSection } from '../types';
import styled from 'styled-components';
import {
  BREAKPOINTS,
  COLORS,
  ComponentSStyling,
  ComponentTextStyle,
  ComponentXSStyling,
} from '../../styles';
import { Size } from '../../types';
import { HyperLink } from '../../HyperLink';
import { useNavigationHelper } from '../../common/NavigationHelper';
import { MenuItem } from '../../MenuItem';
import { SystemIcons } from '../../icons';
import { MobileHeaderContainer, MobileHeaderNote, MobileHeaderText, MobileMenuWrapper, ScrollableContainer } from './CommonStyles';
import { useScrollableContainer } from '../../common/ScrollableContainer';
import { MobileActionContainer } from './MobileActionContainer';
import {useMediaMatch} from "rooks";

const MenuItemsContainer = styled.div`
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  padding: 8px;
`;

const MenuSectionContainer = styled.div`
  padding-bottom: 8px;
  display: flex;
  flex-direction: column;
  width: 100%;

  &.divider {
    border-top: 1px solid ${COLORS.neutral_200};
  }
`;

const MenuSectionHeader = styled.div`
  display: flex;
  align-items: center;
  padding: 8px 16px 4px 16px;
  box-sizing: border-box;
  min-height: 32px;
  background-color: ${COLORS.neutral_20};

  ${ComponentSStyling(ComponentTextStyle.Bold, COLORS.neutral_500)}
`;

const MenuSectionList = styled.div`
  display: flex;
  flex-direction: column;
`;

const PinnedContainer = styled.div`
  border-top: 1px solid ${COLORS.neutral_200};
`;

const FooterText = styled.div`
  ${ComponentXSStyling(ComponentTextStyle.Bold, COLORS.neutral_600)}
  ${BREAKPOINTS.SMALL} {
    ${ComponentSStyling(ComponentTextStyle.Bold, COLORS.neutral_600)}
  }
`;
const FooterNote = styled.div`
  ${ComponentXSStyling(ComponentTextStyle.Regular, COLORS.neutral_600)}
  ${BREAKPOINTS.SMALL} {
    ${ComponentSStyling(ComponentTextStyle.Regular, COLORS.neutral_600)}
  }
`;
const FooterLink = styled.div`
  a {
    ${ComponentXSStyling(ComponentTextStyle.Bold, null)}
    ${BREAKPOINTS.SMALL} {
      ${ComponentSStyling(ComponentTextStyle.Bold, null)}
    }
  }
`;

const FooterContainer = styled.div`
  padding: 8px 16px;
  box-sizing: border-box;
  border-top: 1px solid ${COLORS.neutral_200};
  display: flex;
  flex-direction: column;
  background: ${COLORS.neutral_20};

  ${BREAKPOINTS.SMALL} {
    padding: 16px;
  }
`;

interface MobileMenuContentProps {
  header?: MenuGroupHeader;
  items?: (MenuNavigationItemTypeItem | MenuNavigationItemTypeGroup | MenuNavigationItemTypeSection)[];
  footer?: MenuGroupFooter;
  action?: MenuButton;

  onSubMenuOpen?: (item: MenuNavigationItemTypeGroup) => void;
}

const MobileMenuContent: React.FunctionComponent<MobileMenuContentProps> = ({ header, items, footer, action, onSubMenuOpen }) => {
  const isSmallScreen = useMediaMatch(BREAKPOINTS.SMALL.replace('@media ', ''));

  const { navigate, isActiveRoute } = useNavigationHelper();

  const [scrollContainerRef, scrollable] = useScrollableContainer([header, footer, action, items]);

  const renderItem = (item: MenuNavigationItemTypeItem | MenuNavigationItemTypeGroup | MenuNavigationItemTypeSection, index: number) => {
    switch (item.type) {
      case 'section':
        return (
          <MenuSectionContainer key={index}>
            {item.label && <MenuSectionHeader>{item.label}</MenuSectionHeader>}
            <MenuSectionList>{item.items.map((item, index) => renderItem(item, index))}</MenuSectionList>
          </MenuSectionContainer>
        );
      case 'group':
        return (
          <MenuItem
            key={index}
            id={'menu-item-' + index}
            size={isSmallScreen ? Size.Medium : Size.Small}
            active={false}
            item={{
              value: item.label,
              disabled: item.disabled,
              icon: item.icon,
              displayLabel: item.label,
              noteLabel: item.note,
            }}
            iconRight={<SystemIcons.ArrowDropRight />}
            onClickHandler={(e) => {
              e.nativeEvent.stopImmediatePropagation();
              onSubMenuOpen && onSubMenuOpen(item);
            }}
          />
        );
      case 'item':
      default:
        return (
          <MenuItem
            key={index}
            id={'menu-item-' + index}
            active={!item.external && isActiveRoute(item.to, !!item.exact)}
            size={isSmallScreen ? Size.Medium : Size.Small}
            item={{
              value: item.to,
              disabled: item.disabled,
              icon: item.icon,
              displayLabel: item.label,
              noteLabel: item.note,
            }}
            onClickHandler={(e) => {
              item.action && item.action(e);
              navigate(item.to, !!item.external);
            }}
          />
        );
    }
  };

  return (
    <MobileMenuWrapper>
      <ScrollableContainer ref={scrollContainerRef as any} className={scrollable ? 'scrollable' : ''}>
        {!!header && (
          <MobileHeaderContainer>
            <MobileHeaderText>{header.header}</MobileHeaderText>
            {header.note && <MobileHeaderNote>{header.note}</MobileHeaderNote>}
          </MobileHeaderContainer>
        )}
        <MenuItemsContainer>{items?.filter((a) => !a.pinned).map((item, index) => renderItem(item, index))}</MenuItemsContainer>
      </ScrollableContainer>
      {items?.some((a) => a.pinned) && (
        <PinnedContainer>
          <MenuItemsContainer>{items?.filter((a) => !!a.pinned).map((item, index) => renderItem(item, index))}</MenuItemsContainer>
        </PinnedContainer>
      )}
      {footer && (
        <FooterContainer>
          <FooterText>{footer.header}</FooterText>
          {footer.note && <FooterNote>{footer.note}</FooterNote>}
          {footer.link && (
            <FooterLink>
              <HyperLink variant={'default'} id={footer.link.id} target={footer.link.target} onClick={footer.link.onClick} href={footer.link.href}>
                {footer.link.label}
              </HyperLink>
            </FooterLink>
          )}
        </FooterContainer>
      )}
      {action && <MobileActionContainer {...action} />}
    </MobileMenuWrapper>
  );
};

export default MobileMenuContent;
